import { useSelector } from "react-redux";
import _ from "lodash";
import { PMModulesEnabled, PORTAL_FULL_SEAT, PORTAL_NO_SEAT } from "helpers";
import { RootState } from "@Reducers/index";

const useUserModuleAccess = () => {
  const userDetails = useSelector((state: RootState) => state.userDetails);
  const portalData = useSelector((state: RootState) => state.portalData);
  const currentSelectedSidebarCell = useSelector((state: RootState) => state.currentSelectedSidebarCell);
  const projectSideBarIconsAndNameMapper = useSelector((state: RootState) => state.projectSideBarIconsAndNameMapper);
  const customModulesFromStore = useSelector((state: RootState) => state.customModules);
  const customModules = customModulesFromStore === void 0 ? [] : customModulesFromStore;

  const isReady = Boolean(
    userDetails?._id &&
    portalData?._id &&
    // The projectSideBarIconsAndNameMapper slice is still untyped (its reducer is declared `any` in
    // reducers/projectSetting.ts); only `id` is read here.
    (projectSideBarIconsAndNameMapper as { id?: string }[]).some((icon) => icon.id == currentSelectedSidebarCell),
  );

  // RETURN LEFT AS `any[]`, and it is load-bearing: this yields the numeric seat constants
  // (PORTAL_FULL_SEAT = 1 / PORTAL_NO_SEAT = 0), `null` before the slices load, and a boolean
  // partial-seat flag — but `ModuleAccess` in features/transmittals/hooks/transmittal.types.ts
  // declares `portalSeat: string`. That declaration is wrong and is outside this module, so an
  // honest return type breaks TransmitalDetails / listTransmittals. REAL BUG, reported.
  //
  // RE-ATTEMPTED. Return position, so method shorthand does not apply. The blocker is a wrong
  // DECLARATION elsewhere (`ModuleAccess.portalSeat: string` in features/transmittals), not a
  // variance problem — the fix is to correct that declaration to the numeric seat constants.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- KNOWN BUG: ModuleAccess.portalSeat is declared string but holds the numeric seat constant
  const getUserSeat = (): any[] => {
    if (isReady) {
      const lineItems = _.get(portalData, "subscription.lineItems", []) as Array<{ type: string; code: string }>;
      const planCodes: string[] = lineItems.filter((i) => i.type === "plan").map((i) => i.code);
      if (!planCodes.length) {
        const fallback = _.get(portalData, "subscription.plan") as string | undefined;
        if (fallback) planCodes.push(fallback);
      }

      if (!planCodes.length) return [PORTAL_FULL_SEAT];

      const userSeats = planCodes.map((code: string) => ({
        code,
        // `accounts.portal[].seat` is a plan-code → seat-number map; the path is built at runtime,
        // so lodash cannot resolve it and the read is asserted to the stored value type.
        seat: _.get(userDetails, `seat.${code}`) as number | undefined,
      }));

      const hasSeat = userSeats.some((s) => s.seat !== undefined);
      if (!hasSeat) return [PORTAL_FULL_SEAT];

      if (customModules.some((module) => module.name == currentSelectedSidebarCell)) {
        return [PORTAL_FULL_SEAT];
      }

      // For PM modules, check seat in PM plan(s)
      if (PMModulesEnabled.includes(currentSelectedSidebarCell)) {
        const pmPlanSeat = userSeats.find((s) => s.code.includes("PM"));
        if (pmPlanSeat && pmPlanSeat.seat !== undefined) {
          if ([PORTAL_FULL_SEAT, PORTAL_NO_SEAT].includes(pmPlanSeat.seat)) {
            return [pmPlanSeat.seat];
          }
          return [PORTAL_NO_SEAT, true];
        }
        return [PORTAL_NO_SEAT];
      }

      // For non-PM modules, use the best seat across all plans
      const bestSeat = userSeats.reduce((best: number | undefined, s) => {
        if (s.seat === PORTAL_FULL_SEAT) return PORTAL_FULL_SEAT;
        if (best === undefined) return s.seat;
        return best;
      }, undefined);

      // `bestSeat` is undefined when no plan carried a seat; `includes(undefined) === false` is the
      // intent, so the array is widened rather than the argument asserted.
      if (([PORTAL_FULL_SEAT, PORTAL_NO_SEAT] as (number | undefined)[]).includes(bestSeat)) {
        return [bestSeat];
      }
      return [PORTAL_FULL_SEAT];
    }
    return [null];
  };

  const [portalSeat, isPmPartialSeatPmModule] = getUserSeat();

  return {
    ready: isReady,
    portalSeat,
    isPmPartialSeatPmModule,
  };
};

export default useUserModuleAccess;
